import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { ControleAguaForm, ControleAguaSchema } from "../../../schemas/Obras_sustentabilidade/agua_schema";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormNumericInput } from "../../../../../../common/components/default_form_numeric_input";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";

type Props = {
  open: boolean;
  saving: boolean;
  editmod: boolean;
  onClose: () => void;
  onSubmit: (form: ControleAguaForm) => void;
  dados?: ControleAguaForm | null;
};

export const ModalAgua = ({ open, editmod, onClose, saving, onSubmit, dados }: Props) => {
  const hookForm = useForm<ControleAguaForm>({
    mode: "onSubmit",
    resolver: zodResolver(ControleAguaSchema),
  });

  const { reset, control, handleSubmit } = hookForm;
  const mesesOptions = [
    { id: "1", label: "Janeiro" },
    { id: "2", label: "Fevereiro" },
    { id: "3", label: "Março" },
    { id: "4", label: "Abril" },
    { id: "5", label: "Maio" },
    { id: "6", label: "Junho" },
    { id: "7", label: "Julho" },
    { id: "8", label: "Agosto" },
    { id: "9", label: "Setembro" },
    { id: "10", label: "Outubro" },
    { id: "11", label: "Novembro" },
    { id: "12", label: "Dezembro" },
  ];
  useEffect(() => {
    if (open) {
      if (dados) {
        reset(dados);
      } else {
        reset({
          ano: null,
          idDescricaoMes: null,
          volumeM3: null,
          valor: null,
          efetivoMes: null,
          mediaTrabalhador: null,
          areaConstruida: null,
          mediaArea: null,
          metaAprovacao: null,
          observacao: null,
        });
      }
    }
  }, [open, dados, reset]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} sx={{ "& .MuiPaper-root": { width: "100%", maxWidth: "60rem" } }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{editmod ? "Editar Controle de Água" : "Cadastrar Controle de Água"}</DialogTitle>

        <DialogContent dividers>
          <ModalLoading loading={saving}>
            <ModalContainer>
              <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={6} rowGap={3} mt={1}>
                {/* ESQUERDA */}
                <FormMaskedInput name="ano" label="Ano *" reactHookForm={hookForm} mask="0000" required={true} maskPlaceholder={"____"} />
                <Controller
                  name="idDescricaoMes"
                  control={control}
                  render={({ field }) => (
                    <DefaultFormSelectInput
                      name="idDescricaoMes"
                      label="Mês"
                      required={true}
                      reactHookForm={hookForm}
                      options={[
                        { id: 1, label: "Janeiro" },
                        { id: 2, label: "Fevereiro" },
                        { id: 3, label: "Março" },
                        { id: 4, label: "Abril" },
                        { id: 5, label: "Maio" },
                        { id: 6, label: "Junho" },
                        { id: 7, label: "Julho" },
                        { id: 8, label: "Agosto" },
                        { id: 9, label: "Setembro" },
                        { id: 10, label: "Outubro" },
                        { id: 11, label: "Novembro" },
                        { id: 12, label: "Dezembro" },
                      ]}
                    />
                  )}
                />

                <DefaultFormNumericInput name="volumeM3" label="Volume da Água em M³" required={true} reactHookForm={hookForm} />

                <FormMoneyMaskedInput name="valor" label="Valor" reactHookForm={hookForm} />

                <FormMaskedInput
                  name="diasMedicao"
                  label="Dias de Medição"
                  reactHookForm={hookForm}
                  mask="000000"
                  maskPlaceholder={"______"}
                />

                <FormMaskedInput
                  name="efetivoMes"
                  label="Efetivo do Mês"
                  readOnly={true}
                  reactHookForm={hookForm}
                  mask="000000"
                  maskPlaceholder={"______"}
                />

                <DefaultFormNumericInput name="areaConstruida" label="Área Construída" readOnly={true} reactHookForm={hookForm} />

                <DefaultFormNumericInput name="metaAprovacao" label="Meta de Aprovação" readOnly={true} reactHookForm={hookForm} />
              </Box>

              {/* OBSERVAÇÃO FULL */}
              <Box mt={4}>
                <DefaultFormInput name="observacao" label="Observação" multiline rows={3} reactHookForm={hookForm} />
              </Box>
            </ModalContainer>
          </ModalLoading>
        </DialogContent>

        <DialogActions>
          <Box className="w-full flex justify-end gap-4 p-4">
            <Box
              className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
              onClick={handleClose}
            >
              <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
            </Box>

            <Box width="120px">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">Salvando...</PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </Box>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};
